import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
  MenuItem,
  Chip,
  Stack,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useRef, useState, type ReactNode, type ReactElement } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CloseIcon from "@mui/icons-material/Close";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import ChecklistIcon from "@mui/icons-material/Checklist";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { getClaimBenefit, getDocument } from "../../../../../../services/dashboard";

type SelectionState = {
  [key: string]: string;
};

// Central design tokens for this dialog - a single accent (orange) used sparingly,
// everything else stays neutral. Move to a shared theme file if you want it app-wide.
const ifasColors = {
  accent: "#C94800",
  accentSoft: "#FDEEE5",
  accentBorder: "#F0B27A",
  accentRing: "0 0 0 3px #FDEEE5",
  success: "#3B6D11",
  successSoft: "#F1F9F4",
  successBorder: "#C7E6D4",
  danger: "#B3261E",
  dangerSoft: "#FCEEED",
  dangerBorder: "#F0ADA8",
  neutral: "#6B6B66",
  neutralSoft: "#F3F2EF",
  neutralBorder: "#DDD9D0",
  border: "#DDD9D0",
  divider: "#EBE8E2",
  textPrimary: "#1A1A1A",
  textSecondary: "#5F5E5A",
  textMuted: "#9A9892",
  bgSubtle: "#FCFBF9",
  pageBg: "#F4F3F0",
};

export default function ClaimsBenefitScheduleForm({
  onClaimBenefitCommentChange,
  isAssigned,
  claimStatus,
  claimBenefitComment,
  iceClaimNumber,
  commentData,
  exGratiaFile,
  cashReciept,
  serviceReciept,
  repudiationFile,
  statusReason,
  isEditable,
  claimBenefitInfo,
  claimBenefitData,
  onBenefitTypeChange,
  isOrangeCampaign,
  claimId,
  userId,
  role,
  benefitScheduleOverride,
  submitBenefitOverride,
  approveBenefitOverride,
  rejectBenefitOverride,
  getBenefitOverrideHistory,
  getBenefitInceptionContext,
  onBenefitOverrideSaved,
  showInfoToast,
  showSuccessToast,
}: any) {
  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "CLAIMS_BENEFIT_SCHEDULE",
  );

  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideAction, setOverrideAction] = useState("AMEND_AMOUNT");
  const [selectedBenefitId, setSelectedBenefitId] = useState("");
  const [reasonCode, setReasonCode] = useState("RC_07");
  const [motivation, setMotivation] = useState("");
  const [correctedCash, setCorrectedCash] = useState("");
  const [correctedService, setCorrectedService] = useState("");
  const [correctedInceptionDate, setCorrectedInceptionDate] = useState("");
  const [inceptionContext, setInceptionContext] = useState<any>(null);
  const [recalculatedBenefit, setRecalculatedBenefit] = useState<any>(null);
  const [inceptionContextLoading, setInceptionContextLoading] = useState(false);
  const [inceptionContextError, setInceptionContextError] = useState("");
  const inceptionRequestRef = useRef(0);
  const [newBenefitName, setNewBenefitName] = useState("");
  const [newBenefitType, setNewBenefitType] = useState("Supplementary");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideDecisionSaving, setOverrideDecisionSaving] = useState("");
  const [overrideHistoryOpen, setOverrideHistoryOpen] = useState(false);
  const [overrideHistory, setOverrideHistory] = useState<any[]>([]);
  const [overrideHistoryLoading, setOverrideHistoryLoading] = useState(false);
  const activeOverride = benefitScheduleOverride;
  const activeOverrideStatus = activeOverride?.status;
  const normalizedUserId = userId ? String(userId).toLowerCase() : "";
  const normalizedInitiatedBy = activeOverride?.initiated_by_user_id
    ? String(activeOverride.initiated_by_user_id).toLowerCase()
    : "";
  const isPendingOverride = activeOverrideStatus === "PENDING_APPROVAL";
  const isApprovedOverride = activeOverrideStatus === "APPROVED";
  const isRejectedOverride = activeOverrideStatus === "REJECTED";
  const isOwnPendingOverride =
    isPendingOverride && normalizedInitiatedBy === normalizedUserId;
  const canReviewPendingOverride =
    isPendingOverride &&
    activeOverride?.risk_tier === "TIER_2" &&
    role === "claim_manager" &&
    normalizedInitiatedBy !== normalizedUserId;

  // One status -> {color, softBg, border, bubbleIcon} mapping, so every place that
  // renders an override status (panel, badge chip, history list) stays in sync.
  const overrideStatusMeta: Record<
    string,
    { color: string; soft: string; border: string; icon: ReactElement }
  > = {
    PENDING_APPROVAL: {
      color: ifasColors.accent,
      soft: ifasColors.accentSoft,
      border: ifasColors.accentBorder,
      icon: <HourglassBottomIcon sx={{ fontSize: 16 }} />,
    },
    APPROVED: {
      color: ifasColors.success,
      soft: ifasColors.successSoft,
      border: ifasColors.successBorder,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
    },
    REJECTED: {
      color: ifasColors.danger,
      soft: ifasColors.dangerSoft,
      border: ifasColors.dangerBorder,
      icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
    },
  };
  const currentStatusMeta =
    overrideStatusMeta[activeOverrideStatus as string] || {
      color: ifasColors.neutral,
      soft: ifasColors.neutralSoft,
      border: ifasColors.neutralBorder,
      icon: <ShieldOutlinedIcon sx={{ fontSize: 16 }} />,
    };
  const overridePanelColor = currentStatusMeta.color;
  const overridePanelBg = currentStatusMeta.soft;
  const overridePanelBorder = currentStatusMeta.border;
  const overrideStatusLabel = activeOverrideStatus?.replaceAll("_", " ");
  const overridePanelTitle = isPendingOverride
    ? "Benefit schedule override pending approval"
    : isApprovedOverride
    ? "Approved benefit schedule override"
    : isRejectedOverride
    ? "Benefit schedule override rejected"
    : "Benefit schedule override";
  const overridePanelMessage = isPendingOverride
    ? "Waiting for second approval. Corrected values are not applied yet."
    : isApprovedOverride
    ? "Corrected values are being used for this claim."
    : "Corrected values are not being used for this claim.";

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const parseAmount = (value: any) => parseFloat(value || "0") || 0;
  const canOverrideSchedule = !isEditable && claimStatus === "NEW";
  const getBenefitRowId = (item: any) =>
    item?.schedule_benefit_id ||
    item?.scheduleBenefitId ||
    item?.item_id ||
    item?.product_id ||
    item?.supplementary_id ||
    item?.product_name ||
    item?.supplementary_name;
  const findScheduleIndex = (schedule: any[], overrideItem: any) =>
    schedule.findIndex((item: any) => {
      const rowId = getBenefitRowId(item);
      return (
        rowId === overrideItem?.original_benefit_id ||
        rowId === overrideItem?.originalBenefitId ||
        item?.product_id === overrideItem?.product_id ||
        item?.supplementary_id === overrideItem?.supplementary_id
      );
    });
  const amountFlags = (cashValue: any, serviceValue: any) => {
    const cash = parseAmount(cashValue);
    const service = parseAmount(serviceValue);
    return {
      cash_only: cash > 0 && service <= 0,
      service_only: service > 0 && cash <= 0,
      cash_and_service: cash > 0 && service > 0,
    };
  };
  const toPendingAddedBenefit = (item: any) => {
    const cash = item?.corrected_cash ?? item?.correctedCash ?? 0;
    const service = item?.corrected_service ?? item?.correctedService ?? 0;
    return {
      schedule_benefit_id:
        item?.item_id ||
        item?.original_benefit_id ||
        item?.product_id ||
        item?.supplementary_id ||
        `${item?.product_name || item?.supplementary_name}-pending`,
      product_id: item?.product_id,
      supplementary_id: item?.supplementary_id,
      product_name: item?.product_name,
      supplementary_name: item?.supplementary_name,
      cash,
      service,
      is_under_waiting_period:
        item?.is_under_waiting_period ?? item?.isUnderWaitingPeriod ?? false,
      ...amountFlags(cash, service),
      is_override: true,
      override_action: item?.action,
      override_status: "PENDING_APPROVAL",
      is_pending_override_preview: true,
    };
  };
  const applyPendingOverridePreview = (schedule: any[] = []) => {
    if (!isPendingOverride || !activeOverride?.items?.length) {
      return schedule || [];
    }
    const preview = schedule.map((item: any) => ({ ...item }));
    activeOverride.items.forEach((item: any) => {
      if (item?.action === "ADD_BENEFIT") {
        preview.push(toPendingAddedBenefit(item));
        return;
      }
      const index = findScheduleIndex(preview, item);
      if (index < 0) return;
      const row = preview[index];
      if (item?.action === "REMOVE_BENEFIT") {
        preview[index] = {
          ...row,
          is_override: true,
          override_action: item.action,
          override_status: "PENDING_APPROVAL",
          is_pending_override_preview: true,
          is_pending_remove: true,
        };
        return;
      }
      const cash = item?.corrected_cash ?? item?.correctedCash ?? row.cash;
      const service = item?.corrected_service ?? item?.correctedService ?? row.service;
      preview[index] = {
        ...row,
        cash,
        service,
        ...amountFlags(cash, service),
        corrected_inception_date:
          item?.corrected_inception_date ?? item?.correctedInceptionDate,
        is_under_waiting_period:
          item?.is_under_waiting_period ??
          item?.isUnderWaitingPeriod ??
          row.is_under_waiting_period,
        is_override: true,
        override_action: item.action,
        override_status: "PENDING_APPROVAL",
        is_pending_override_preview: true,
      };
    });
    return preview;
  };
  const displayedClaimBenefitInfo = applyPendingOverridePreview(claimBenefitInfo || []);
  const selectedBenefit = claimBenefitInfo?.find((item: any) => {
    return getBenefitRowId(item) === selectedBenefitId;
  });
  const resolveRecalculatedBenefit = (benefitResponse: any, benefit: any) => {
    if (!benefitResponse || !benefit) return null;
    if (benefit.product_id || benefit.productId) {
      return benefitResponse.product || null;
    }
    const supplementaryId = String(benefit.supplementary_id || benefit.supplementaryId || "");
    return (benefitResponse.supplementaryData || []).find(
      (item: any) => String(item.supplementary_id || item.supplementaryId || "") === supplementaryId,
    ) || null;
  };
  const getRecalculatedAmounts = (benefit: any, source: any) => {
    if (!benefit || !source) return null;
    const cashAndService = source.cash_and_service || source.cashAndService || {};
    if (benefit.cash_and_service || benefit.cashAndService) {
      return {
        cash: parseAmount(cashAndService.cash),
        service: parseAmount(cashAndService.service),
      };
    }
    if (benefit.cash_only || benefit.cashOnly) {
      return { cash: parseAmount(source.cash_only ?? source.cashOnly), service: 0 };
    }
    if (benefit.service_only || benefit.serviceOnly) {
      return { cash: 0, service: parseAmount(source.service_only ?? source.serviceOnly) };
    }
    return null;
  };
  const recalculatedAmounts = getRecalculatedAmounts(selectedBenefit, recalculatedBenefit);
  const isInceptionCorrection = reasonCode === "RC_01" || reasonCode === "RC_02";
  const calculateCompletedMonths = (startValue: string, endValue: string) => {
    if (!startValue || !endValue) return null;
    const start = new Date(`${startValue}T00:00:00`);
    const end = new Date(`${endValue}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    let months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
    if (end.getDate() < start.getDate()) months -= 1;
    return months;
  };
  const previewCompletedMonths = calculateCompletedMonths(
    correctedInceptionDate,
    inceptionContext?.date_of_death,
  );
  const previewUnderWaitingPeriod = previewCompletedMonths === null
    ? null
    : previewCompletedMonths < Number(inceptionContext?.waiting_period_months || 0);
  const allowsCashCorrection =
    overrideAction === "ADD_BENEFIT" ||
    overrideAction === "AMEND_AMOUNT" ||
    !selectedBenefit ||
    Boolean(selectedBenefit.cash_only || selectedBenefit.cash_and_service);
  const allowsServiceCorrection =
    overrideAction === "ADD_BENEFIT" ||
    overrideAction === "AMEND_AMOUNT" ||
    !selectedBenefit ||
    Boolean(selectedBenefit.service_only || selectedBenefit.cash_and_service);
  const hasInvalidOverrideAmount = [correctedCash, correctedService].some(
    (value) => {
      if (value === "") return false;
      const numericValue = Number(value);
      const decimalPlaces = String(value).split(".")[1]?.length || 0;
      return (
        !Number.isFinite(numericValue) ||
        numericValue < 0 ||
        numericValue > 999999999999.99 ||
        decimalPlaces > 2
      );
    },
  );
  const correctedCashAmount = parseAmount(correctedCash);
  const correctedServiceAmount = parseAmount(correctedService);
  const hasPositiveCorrectedAmount =
    correctedCashAmount > 0 || correctedServiceAmount > 0;
  const isAmendmentUnchanged =
    overrideAction === "AMEND_AMOUNT" &&
    Boolean(selectedBenefit) &&
    correctedCashAmount === parseAmount(selectedBenefit?.cash) &&
    correctedServiceAmount === parseAmount(selectedBenefit?.service) &&
    !correctedInceptionDate;
  const normalizedNewBenefitName = newBenefitName.trim().toLowerCase();
  const isDuplicateAddedBenefit =
    overrideAction === "ADD_BENEFIT" &&
    normalizedNewBenefitName.length > 0 &&
    (claimBenefitInfo || []).some((item: any) => {
      const existingName =
        newBenefitType === "Product"
          ? item?.product_name
          : item?.supplementary_name;
      return String(existingName || "").trim().toLowerCase() === normalizedNewBenefitName;
    });
  const selectableOverrideBenefits =
    overrideAction === "REMOVE_BENEFIT"
      ? (claimBenefitInfo || []).filter(
          (item: any) => !(item?.product_id ?? item?.productId),
        )
      : claimBenefitInfo || [];

  // Filter and sum cash values
  const totalCash = displayedClaimBenefitInfo
    ?.filter((item: any) => !item.is_pending_remove)
    ?.map((item: any) => parseAmount(item.cash))
    .reduce((acc: any, curr: any) => acc + curr, 0);

  // Filter and sum service values
  const totalService = displayedClaimBenefitInfo
    ?.filter((item: any) => !item.is_pending_remove)
    ?.map((item: any) => parseAmount(item.service))
    .reduce((acc: any, curr: any) => acc + curr, 0);
  const productArray =
    displayedClaimBenefitInfo?.filter((item: any) => item.product_id != null) || [];

  const supplementaryViewArray = isEditable
    ? claimBenefitData?.supplementaryData || []
    : displayedClaimBenefitInfo?.filter((item: any) => item.product_id == null) || [];

  const isClaimSubmissionWindowExpired = Boolean(
    claimBenefitData?.isClaimSubmissionWindowExpired ??
      claimBenefitData?.is_claim_submission_window_expired,
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc?.document_id);
        if (!res || res.status !== 200) {
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);
        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const openOverrideHistory = async () => {
    setOverrideHistoryOpen(true);
    setOverrideHistoryLoading(true);
    const res = await getBenefitOverrideHistory?.(claimId);
    setOverrideHistoryLoading(false);
    if (res?.status === 200) {
      setOverrideHistory(res.data || []);
    } else {
      showInfoToast?.(res?.data?.message || "Failed to load override history.");
    }
  };

  const buildOverrideEvidenceDoc = (override: any) => {
    if (!override?.evidence_document_id) return null;
    return {
      document_id: override.evidence_document_id,
      file_name: override.evidence_file_name || "Benefit override evidence",
    };
  };

  const previewOverrideEvidence = (override: any) => {
    const doc = buildOverrideEvidenceDoc(override);
    if (!doc) {
      showInfoToast?.("No evidence document is attached.");
      return;
    }
    handlePreview(doc);
  };

  const downloadOverrideEvidence = (override: any) => {
    const doc = buildOverrideEvidenceDoc(override);
    if (!doc) {
      showInfoToast?.("No evidence document is attached.");
      return;
    }
    handleDownload(doc);
  };

  const resetOverrideForm = () => {
    inceptionRequestRef.current += 1;
    setOverrideAction("AMEND_AMOUNT");
    setSelectedBenefitId("");
    setReasonCode("RC_07");
    setMotivation("");
    setCorrectedCash("");
    setCorrectedService("");
    setCorrectedInceptionDate("");
    setInceptionContext(null);
    setRecalculatedBenefit(null);
    setInceptionContextError("");
    setInceptionContextLoading(false);
    setNewBenefitName("");
    setNewBenefitType("Supplementary");
    setEvidenceFile(null);
  };

  const loadInceptionContext = async (benefitId: string) => {
    const requestNumber = ++inceptionRequestRef.current;
    const benefitForRefresh = (claimBenefitInfo || []).find(
      (item: any) => getBenefitRowId(item) === benefitId,
    );
    setCorrectedInceptionDate("");
    setInceptionContext(null);
    setInceptionContextError("");
    if (!benefitId) return;
    setInceptionContextLoading(true);
    const [res, benefitRes] = await Promise.all([
      getBenefitInceptionContext?.(claimId, benefitId),
      getClaimBenefit(claimId),
    ]);
    if (requestNumber !== inceptionRequestRef.current) return;
    setInceptionContextLoading(false);
    if (res?.status === 200 && res?.data) {
      setInceptionContext(res.data);
      setCorrectedInceptionDate(res.data.easipol_inception_date || "");
      if (benefitRes?.status === 200) {
        setRecalculatedBenefit(resolveRecalculatedBenefit(benefitRes.data, benefitForRefresh));
      }
      return;
    }
    setInceptionContextError(
      getApiErrorMessage(res, "Latest inception date could not be loaded from Easipol."),
    );
  };

  const closeOverrideDialog = () => {
    setOverrideOpen(false);
    resetOverrideForm();
  };

  const buildOverrideItem = () => {
    if (overrideAction === "ADD_BENEFIT") {
      const cash = parseAmount(correctedCash);
      const service = parseAmount(correctedService);
      return {
        action: overrideAction,
        product_id: null,
        supplementary_id: null,
        product_name: newBenefitType === "Product" ? newBenefitName : null,
        supplementary_name:
          newBenefitType === "Supplementary" ? newBenefitName : null,
        corrected_cash: cash,
        corrected_service: service,
        cash_only: cash > 0 && service <= 0,
        service_only: service > 0 && cash <= 0,
        cash_and_service: cash > 0 && service > 0,
        corrected_inception_date: null,
        is_under_waiting_period: false,
      };
    }

    const cash = isInceptionCorrection && recalculatedAmounts
      ? recalculatedAmounts.cash
      : allowsCashCorrection
      ? correctedCash === ""
        ? parseAmount(selectedBenefit?.cash)
        : parseAmount(correctedCash)
      : 0;
    const service = isInceptionCorrection && recalculatedAmounts
      ? recalculatedAmounts.service
      : allowsServiceCorrection
      ? correctedService === ""
        ? parseAmount(selectedBenefit?.service)
        : parseAmount(correctedService)
      : 0;
    return {
      action: overrideAction,
      original_benefit_id:
        selectedBenefit?.schedule_benefit_id || selectedBenefit?.scheduleBenefitId,
      product_id: selectedBenefit?.product_id,
      supplementary_id: selectedBenefit?.supplementary_id,
      product_name: selectedBenefit?.product_name,
      supplementary_name: selectedBenefit?.supplementary_name,
      corrected_cash: cash,
      corrected_service: service,
      cash_only: cash > 0 && service <= 0,
      service_only: service > 0 && cash <= 0,
      cash_and_service: cash > 0 && service > 0,
      corrected_inception_date: isInceptionCorrection ? correctedInceptionDate : null,
      is_under_waiting_period: selectedBenefit?.is_under_waiting_period,
    };
  };

  // ---- UI-only helpers for the redesigned override dialog ----
  const missingFields: string[] = [];
  if (!reasonCode) missingFields.push("Reason code");
  if (!motivation.trim()) missingFields.push("Motivation");
  if (overrideAction === "ADD_BENEFIT" && !newBenefitName.trim()) {
    missingFields.push("Benefit name");
  }
  if (
    overrideAction === "ADD_BENEFIT" &&
    !hasPositiveCorrectedAmount
  ) {
    missingFields.push("Cash or service amount");
  }
  if (hasInvalidOverrideAmount) missingFields.push("Valid non-negative amounts");
  if (isDuplicateAddedBenefit) missingFields.push("Unique benefit name");
  if (overrideAction !== "ADD_BENEFIT" && !selectedBenefit) {
    missingFields.push("Benefit line");
  }
  if (
    overrideAction === "AMEND_AMOUNT" &&
    !isInceptionCorrection &&
    selectedBenefit &&
    !hasPositiveCorrectedAmount
  ) {
    missingFields.push("Cash or service amount");
  }
  if (isAmendmentUnchanged) missingFields.push("Changed amount or payout type");
  if (isInceptionCorrection && !correctedInceptionDate) {
    missingFields.push("Effective inception date");
  }
  if (isInceptionCorrection && !inceptionContext) {
    missingFields.push("Latest Easipol inception data");
  }
  if (isInceptionCorrection && !recalculatedAmounts) {
    missingFields.push("Latest benefit calculation from Easipol");
  }
  if (isInceptionCorrection && previewCompletedMonths !== null && previewCompletedMonths < 0) {
    missingFields.push("Inception date on or before date of death");
  }
  if (!evidenceFile) missingFields.push("Evidence file");
  const isOverrideReadyToSubmit = missingFields.length === 0;

  const actionLabels: Record<string, string> = {
    AMEND_AMOUNT: "Amend amount",
    ADD_BENEFIT: "Add missing benefit",
    REMOVE_BENEFIT: "Remove benefit",
  };

  const formatOverrideDate = (value: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatOverrideStatus = (value: string) =>
    value ? value.replaceAll("_", " ") : "-";

  const getApiErrorMessage = (res: any, fallback: string) =>
    res?.data?.message ||
    res?.data?.errorMessage ||
    res?.data?.localizedMessage ||
    res?.data?.error ||
    res?.data?.detail ||
    res?.data?.title ||
    res?.response?.data?.message ||
    res?.response?.data?.error ||
    res?.message ||
    res?.data?.violations?.[0]?.message ||
    res?.data?.errors?.[0]?.defaultMessage ||
    (typeof res?.data === "string" ? res.data : "") ||
    fallback;

  const formatOverrideItemSummary = (item: any) => {
    if (!item) return "";
    const name =
      item.product_name ||
      item.supplementary_name ||
      item.productName ||
      item.supplementaryName ||
      "Benefit";
    const action = (item.corrected_inception_date ?? item.correctedInceptionDate)
      ? "Correct inception date"
      : actionLabels[item.action] || item.action || "Correction";
    const cash = item.corrected_cash ?? item.correctedCash;
    const service = item.corrected_service ?? item.correctedService;
    const parts = [action, name];
    if (cash !== null && cash !== undefined) parts.push(`Cash R${cash}`);
    if (service !== null && service !== undefined) parts.push(`Service R${service}`);
    const originalDate = item.original_inception_date ?? item.originalInceptionDate;
    const correctedDate = item.corrected_inception_date ?? item.correctedInceptionDate;
    if (correctedDate) parts.push(`Inception ${originalDate || "-"} to ${correctedDate}`);
    if (correctedDate) {
      const waiting = item.is_under_waiting_period ?? item.isUnderWaitingPeriod;
      const requiredMonths = item.waiting_period_months ?? item.waitingPeriodMonths;
      const completedMonths = item.completed_months ?? item.completedMonths;
      if (requiredMonths !== null && requiredMonths !== undefined) {
        parts.push(`${completedMonths ?? "-"}/${requiredMonths} months completed`);
      }
      parts.push(waiting ? "Still waiting" : "Eligible");
    }
    return parts.join(" - ");
  };

  const handleSubmitOverride = async () => {
    if (!reasonCode || !motivation.trim()) {
      showInfoToast?.("Reason and motivation are required.");
      return;
    }
    if (overrideAction === "ADD_BENEFIT" && !newBenefitName.trim()) {
      showInfoToast?.("Benefit name is required.");
      return;
    }
    if (
      overrideAction === "ADD_BENEFIT" &&
      !hasPositiveCorrectedAmount
    ) {
      showInfoToast?.("Cash or service amount is required.");
      return;
    }
    if (hasInvalidOverrideAmount) {
      showInfoToast?.("Enter valid non-negative amounts with up to two decimal places.");
      return;
    }
    if (isDuplicateAddedBenefit) {
      showInfoToast?.("A benefit with the same type and name already exists.");
      return;
    }
    if (overrideAction !== "ADD_BENEFIT" && !selectedBenefit) {
      showInfoToast?.("Please select a benefit line.");
      return;
    }
    if (
      overrideAction === "AMEND_AMOUNT" &&
      !isInceptionCorrection &&
      !hasPositiveCorrectedAmount
    ) {
      showInfoToast?.("An amended benefit must retain a cash or service amount. Use Remove Benefit instead.");
      return;
    }
    if (isAmendmentUnchanged) {
      showInfoToast?.("Enter a different cash or service amount before submitting.");
      return;
    }
    if (isInceptionCorrection && (!correctedInceptionDate || !inceptionContext)) {
      showInfoToast?.("Load and confirm the effective inception date before submitting.");
      return;
    }
    if (isInceptionCorrection && !recalculatedAmounts) {
      showInfoToast?.("The latest benefit amount could not be calculated from Easipol data.");
      return;
    }
    if (!evidenceFile) {
      showInfoToast?.("Evidence document is required.");
      return;
    }

    setOverrideSaving(true);
    const payload = {
      user_id: userId,
      reason_code: reasonCode,
      motivation,
      items: [buildOverrideItem()],
    };
    const res = await submitBenefitOverride?.(claimId, payload, evidenceFile);
    setOverrideSaving(false);
    if (res?.status === 200) {
      const status = res.data?.status;
      if (status === "PENDING_APPROVAL") {
        showInfoToast?.("Tier 2 override sent for second approval.");
      } else {
        showSuccessToast?.("Benefit schedule override approved.");
      }
      closeOverrideDialog();
      await onBenefitOverrideSaved?.();
    } else {
      showInfoToast?.(getApiErrorMessage(res, "Failed to save benefit override."));
    }
  };

  const handleOverrideDecision = async (approved: boolean) => {
    if (!activeOverride?.override_id) return;
    setOverrideDecisionSaving(approved ? "approve" : "reject");
    const payload = { user_id: userId };
    const res = approved
      ? await approveBenefitOverride?.(claimId, activeOverride.override_id, payload)
      : await rejectBenefitOverride?.(claimId, activeOverride.override_id, payload);
    setOverrideDecisionSaving("");
    if (res?.status === 200) {
      if (approved) {
        showSuccessToast?.("Benefit schedule override approved.");
      } else {
        showInfoToast?.("Benefit schedule override rejected.");
      }
      await onBenefitOverrideSaved?.();
    } else {
      showInfoToast?.(getApiErrorMessage(res, "Failed to review benefit override."));
    }
  };

  // Small reusable section header used inside the override dialog - a short orange
  // accent bar next to an uppercase, letter-spaced label, matching the house style
  // used across other checklist/form dialogs in the app.
  const SectionHeader = ({
    icon,
    title,
    hint,
  }: {
    icon?: ReactNode;
    title: string;
    hint?: string;
  }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box
        sx={{
          width: "3px",
          height: 13,
          borderRadius: "2px",
          bgcolor: ifasColors.accent,
          flexShrink: 0,
        }}
      />
      <Typography
        fontWeight={700}
        fontSize={11.5}
        letterSpacing="0.06em"
        color={ifasColors.accent}
        sx={{ textTransform: "uppercase" }}
      >
        {title}
      </Typography>
      {hint && (
        <Typography fontSize={11.5} color={ifasColors.textMuted}>
          — {hint}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Typography
        textAlign="left"
        fontWeight={700}
        fontSize={16}
        mb={1}
        color="red"
      >
        {isOrangeCampaign && "(Policy is created under Orange Campaign)"}
      </Typography>

      {(canOverrideSchedule || activeOverride) && (role === "claim_manager" || role === "underwriter" || role === "finance") && (
        <Box
          sx={{
            width: { xs: "100%", sm: "80%", lg: "80%" },
            display: "flex",
            justifyContent: "flex-end",
            mb: 1,
          }}
        >
          {canOverrideSchedule && role === "claim_manager" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditNoteIcon />}
              onClick={() => setOverrideOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "999px",
                px: 2.5,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Override Benefit Schedule
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ChecklistIcon />}
            onClick={openOverrideHistory}
            sx={{
              ml: 1,
              textTransform: "none",
              borderRadius: "999px",
              px: 2.25,
              bgcolor: "#fff",
              color: ifasColors.accent,
              borderColor: ifasColors.accent,
              boxShadow: "none",
              "&:hover": {
                bgcolor: ifasColors.accentSoft,
                borderColor: ifasColors.accent,
                boxShadow: "none",
              },
            }}
          >
            Override History
          </Button>
        </Box>
      )}

      {(activeOverride || claimBenefitInfo?.some((item: any) => item.is_override)) && (role === "claim_manager" || role === "underwriter" || role === "finance") && (
        <Box
          sx={{
            width: { xs: "100%", sm: "80%" },
            mb: 2,
            px: 2,
            py: 1.35,
            border: `1px solid ${overridePanelBorder}`,
            borderLeft: `5px solid ${overridePanelColor}`,
            borderRadius: 1.5,
            background: overridePanelBg,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-start" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: "1 1 auto" }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  color: overridePanelColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.1,
                  border: `1px solid ${overridePanelBorder}`,
                }}
              >
                {currentStatusMeta.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography fontWeight={800} color={ifasColors.textPrimary}>
                    {overridePanelTitle}
                  </Typography>
                  {activeOverride?.status && (
                    <Chip
                      size="small"
                      label={overrideStatusLabel}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#FFFFFF",
                        backgroundColor: overridePanelColor,
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color={ifasColors.textSecondary} sx={{ mt: 0.25 }}>
                  {overridePanelMessage}
                </Typography>
                {activeOverride?.motivation && (
                  <Typography
                    variant="body2"
                    color={ifasColors.textPrimary}
                    sx={{
                      mt: 0.75,
                      maxWidth: 720,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    <Box component="span" sx={{ color: ifasColors.textMuted }}>
                      Justification:
                    </Box>{" "}
                    {activeOverride.motivation}
                  </Typography>
                )}
                {!!activeOverride?.items?.length && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {activeOverride.items.map((item: any) => (
                      <Chip
                        key={item.item_id || item.original_benefit_id || formatOverrideItemSummary(item)}
                        size="small"
                        label={formatOverrideItemSummary(item)}
                        sx={{
                          maxWidth: "100%",
                          backgroundColor: "#FFFFFF",
                          border: `1px solid ${overridePanelBorder}`,
                          color: ifasColors.textPrimary,
                          fontWeight: 700,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>

            {activeOverride && (
              <Stack
                spacing={0.9}
                alignItems={{ xs: "flex-start", md: "flex-end" }}
                sx={{ flexShrink: 0, maxWidth: { xs: "100%", md: 520 } }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    size="small"
                    label={`Reason: ${activeOverride.reason_code || "-"}`}
                    sx={{ backgroundColor: "#FFFFFF", fontWeight: 700, border: `1px solid ${ifasColors.border}` }}
                  />
                  <Chip
                    size="small"
                    label={`Tier: ${activeOverride.risk_tier || "-"}`}
                    sx={{ backgroundColor: "#FFFFFF", fontWeight: 700, border: `1px solid ${ifasColors.border}` }}
                  />
                  <Chip
                    size="small"
                    icon={<InsertDriveFileOutlinedIcon />}
                    label={activeOverride.evidence_file_name || "Evidence not attached"}
                    sx={{
                      maxWidth: 250,
                      backgroundColor: "#FFFFFF",
                      border: `1px solid ${ifasColors.border}`,
                      color: activeOverride.evidence_file_name
                        ? ifasColors.textPrimary
                        : ifasColors.textMuted,
                      fontWeight: 700,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                  {activeOverride.evidence_document_id && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => previewOverrideEvidence(activeOverride)}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: "#FFFFFF",
                          border: `1px solid ${overridePanelBorder}`,
                        }}
                        title="View evidence"
                      >
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => downloadOverrideEvidence(activeOverride)}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: "#FFFFFF",
                          border: `1px solid ${overridePanelBorder}`,
                        }}
                        title="Download evidence"
                      >
                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Stack>
                {isPendingOverride && (
                  canReviewPendingOverride ? (
                    <Stack direction="row" spacing={0.75}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={!!overrideDecisionSaving}
                        onClick={() => handleOverrideDecision(true)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "999px",
                          backgroundColor: ifasColors.success,
                          boxShadow: "none",
                          px: 1.75,
                          "&:hover": {
                            backgroundColor: "#2E5A0D",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {overrideDecisionSaving === "approve" ? "Approving..." : "Approve"}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CancelOutlinedIcon />}
                        disabled={!!overrideDecisionSaving}
                        onClick={() => handleOverrideDecision(false)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "999px",
                          borderColor: ifasColors.danger,
                          color: ifasColors.danger,
                          px: 1.75,
                          backgroundColor: "#FFFFFF",
                          "&:hover": {
                            backgroundColor: ifasColors.dangerSoft,
                            borderColor: ifasColors.danger,
                          },
                        }}
                      >
                        {overrideDecisionSaving === "reject" ? "Rejecting..." : "Reject"}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip
                        size="small"
                        icon={<HourglassBottomIcon sx={{ fontSize: 14 }} />}
                        label={
                          isOwnPendingOverride
                            ? "Awaiting another Claim Manager"
                            : "Claim Manager review required"
                        }
                        sx={{
                          backgroundColor: "#FFFFFF",
                          color: overridePanelColor,
                          border: `1px solid ${overridePanelBorder}`,
                          fontWeight: 800,
                          "& .MuiChip-icon": { color: overridePanelColor },
                        }}
                      />
                    </Stack>
                  )
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      )}

  {isClaimSubmissionWindowExpired && (
  <Box
    sx={{
      width: { xs: "100%", sm: "80%" },
      background: "linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)",
      border: "1px solid #ffcdd2",
      borderLeft: "5px solid #F44336",
      borderRadius: 2,
      p: 2.5,
      mb: 3,
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #F44336, #C62828)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 2px 8px rgba(244,67,54,0.35)",
      }}
    >
      <Typography
        color="white"
        fontWeight={800}
        fontSize={20}
        lineHeight={1}
      >
        !
      </Typography>
    </Box>

    {/* Text */}
    <Box textAlign="left">
      <Typography
        fontWeight={700}
        fontSize={15}
        color="#C62828"
        mb={0.5}
      >
        Claim Submission Window Expired
      </Typography>
      <Typography fontSize={13} color="#7f0000" lineHeight={1.7}>
        This claim was submitted after the allowed{" "}
        <Box
          component="span"
          sx={{
            backgroundColor: "#ffcdd2",
            px: 0.7,
            py: 0.2,
            borderRadius: 1,
            fontWeight: 700,
            color: "#C62828",
          }}
        >
          180-day window
        </Box>{" "}
        from the date of death and{" "}
        <strong>cannot be approved.</strong>
      </Typography>
    </Box>
  </Box>
)}

      {isEditable ? (
        <>
          <Box mb={4}>
            <Typography fontWeight={600} fontSize={18} mb={2} textAlign="left">
              Choose the type of benefits
            </Typography>
            <RadioGroup
              row
              value={claimBenefitData?.product?.selectedType || ""}
              onChange={(e) => {
                onBenefitTypeChange(e.target.value);
              }}
            >
              {claimBenefitData?.product?.service_only && (
                <FormControlLabel
                  value="Services Only"
                  control={<Radio color="warning" />}
                  label="Services Only"
                  sx={{
                    "& .MuiTypography-root": { fontWeight: 400 },
                  }}
                />
              )}
              {claimBenefitData?.product?.cash_only && (
                <FormControlLabel
                  value="Cash Only"
                  control={<Radio color="warning" />}
                  label="Cash Only"
                  sx={{
                    "& .MuiTypography-root": { fontWeight: 400 },
                  }}
                />
              )}
              {claimBenefitData?.product?.cash_and_service &&
                Object.keys(claimBenefitData.product.cash_and_service).length >
                  0 && (
                  <FormControlLabel
                    value="cash_and_service"
                    control={<Radio color="warning" />}
                    label="Service + Cash"
                    sx={{
                      "& .MuiTypography-root": { fontWeight: 400 },
                    }}
                  />
                )}
            </RadioGroup>
          </Box>
          {supplementaryViewArray.map((item: any) => {
            return (
              <Box key={getBenefitRowId(item)}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid size={{ xs: 4 }}>
                    <Typography
                      fontWeight={600}
                      fontSize={16}
                      textAlign="left"
                      ml={2}
                    >
                      {item.supplementary_name}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4 }}>
                    <Typography fontWeight={400} fontSize={16}>
                      {item.selectedType
                        ? item.selectedType == "Cash"
                          ? `R${item.cash_only}`
                          : `R${item.service_only}`
                        : "R0"}
                    </Typography>
                  </Grid>

                  <Grid
                    size={{ xs: 4 }}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <RadioGroup
                      row
                      value={item.selectedType || ""}
                      onChange={(e) => {
                        onBenefitTypeChange({
                          supplementaryId: item.supplementary_id,
                          type: e.target.value,
                        });
                      }}
                    >
                      <FormControlLabel
                        value="Cash"
                        control={<Radio color="warning" />}
                        label={
                          <Typography fontSize={16}>Cash Payout</Typography>
                        }
                        // disabled={
                        //   item.cash_only || item.service_only || item.service
                        // }
                        sx={{ mr: 4 }}
                      />
                      <FormControlLabel
                        value="Service"
                        control={<Radio color="warning" />}
                        label={<Typography fontSize={16}>Service</Typography>}
                        // disabled={
                        //   item.cash_only || item.service_only || item.service
                        // }
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </>
      ) : (
        <>
          <Box
            sx={{
              width: { xs: "100%", sm: "80%", lg: "80%" },
              border: "1px solid #DFDDDD",
              my: 3,
            }}
          >
            {productArray.map((item: any) => {
              return (
                <Box
                  key={getBenefitRowId(item)}
                  sx={{
                    bgcolor: "#f5f5f5",
                    p: 2,
                    borderRadius: 1,
                    mb: 2,
                    border: "none",
                    opacity: item.is_pending_remove ? 0.72 : 1,
                  }}
                >
                  <Typography
                    textAlign="left"
                    fontWeight={700}
                    fontSize={16}
                    mb={1}
                  >
                    Product
                  </Typography>
                  <Typography
                    textAlign="left"
                    fontWeight={700}
                    fontSize={16}
                    mb={1}
                    color="red"
                  >
                    {item.is_under_waiting_period &&
                      " (Policy is under waiting period)"}
                  </Typography>
                  <Grid container>
                    <Grid size={{ xs: 6 }} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography fontWeight={600} fontSize={16} align="left">
                        {item.product_name || "Product Name"}
                      </Typography>
                      {item.is_pending_override_preview && (
                        <Chip
                          size="small"
                          label={
                            item.is_pending_remove
                              ? "Pending removal"
                              : item.override_action === "ADD_BENEFIT"
                              ? "Pending added benefit"
                              : "Pending correction"
                          }
                          sx={{
                            mt: 0.75,
                            backgroundColor: "#FFFFFF",
                            color: ifasColors.accent,
                            border: `1px solid ${ifasColors.accentBorder}`,
                            fontWeight: 800,
                          }}
                        />
                      )}
                    </Grid>
                    <Grid
                      size={{ xs: 6 }}
                      display="flex"
                      justifyContent="flex-end"
                      gap={1}
                      flexDirection="column"
                    >
                      <Typography
                        align="right"
                        fontSize={18}
                        fontWeight={600}
                        color="#EC6E00"
                      >
                        {item.cash_and_service
                          ? "Cash and Service"
                          : item.cash_only
                            ? "Cash Only"
                            : item.service_only
                              ? "Service Only"
                              : "-"}
                      </Typography>
                      <Box textAlign="right">
                        {item.cash_and_service ? (
                          <>
                            <Typography fontSize={16}>
                              Cash Amount: R{item.cash || 0}
                            </Typography>
                            <Typography fontSize={16}>
                              Service Amount: R{item.service || 0}
                            </Typography>
                          </>
                        ) : item.cash_only ? (
                          <Typography fontSize={16}>
                            Cash Amount: R{item.cash || 0}
                          </Typography>
                        ) : item.service_only ? (
                          <Typography fontSize={16}>
                            Service Amount: R{item.service || 0}
                          </Typography>
                        ) : (
                          <Typography fontSize={18}>-</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

            {/* Topup rows */}
            <Typography textAlign="left" fontWeight={700} fontSize={16} ml={2}>
              TopUps
            </Typography>
            {supplementaryViewArray.map((item: any) => {
              return (
                <Box
                  key={getBenefitRowId(item)}
                  sx={{
                    mx: 0,
                    my: 0,
                    py: 0,
                    borderRadius: 0,
                    backgroundColor: "transparent",
                    border: "none",
                    opacity: item.is_pending_remove ? 0.72 : 1,
                  }}
                >
                  <Typography
                    textAlign="left"
                    fontWeight={700}
                    fontSize={16}
                    color="red"
                    ml={2}
                  >
                    {item.is_under_waiting_period &&
                      " (Top-up is under waiting period)"}
                  </Typography>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid size={{ xs: 4 }} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography
                        fontWeight={600}
                        fontSize={16}
                        textAlign="left"
                        ml={2}
                      >
                        {item.supplementary_name}
                      </Typography>
                      {item.is_pending_override_preview && (
                        <Chip
                          size="small"
                          label={
                            item.is_pending_remove
                              ? "Pending removal"
                              : item.override_action === "ADD_BENEFIT"
                              ? "Pending added benefit"
                              : "Pending correction"
                          }
                          sx={{
                            mt: 0.75,
                            ml: 2,
                            backgroundColor: "#FFFFFF",
                            color: ifasColors.accent,
                            border: `1px solid ${ifasColors.accentBorder}`,
                            fontWeight: 800,
                          }}
                        />
                      )}
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography fontWeight={400} fontSize={16}>
                        {item.cash_only
                          ? `R${item.cash || 0}`
                          : item.service_only || item.service
                            ? `R${item.service || 0}`
                            : "-"}
                      </Typography>
                    </Grid>

                    <Grid
                      size={{ xs: 4 }}
                      display="flex"
                      justifyContent="flex-end"
                    >
                      <RadioGroup
                        row
                        value={
                          item.cash_only
                            ? "Cash"
                            : item.service_only || item.service
                              ? "Service"
                              : ""
                        }
                      >
                        <FormControlLabel
                          value="Cash"
                          control={<Radio color="warning" />}
                          label={
                            <Typography fontSize={16}>Cash Payout</Typography>
                          }
                          disabled={
                            item.cash_only || item.service_only || item.service
                          }
                          sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                          value="Service"
                          control={<Radio color="warning" />}
                          label={<Typography fontSize={16}>Service</Typography>}
                          disabled={
                            item.cash_only || item.service_only || item.service
                          }
                        />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
            <Divider sx={{ my: 2, mx: 3 }} />

            <Grid container spacing={1}>
              {totalCash !== 0 && (
                <Grid
                  size={{ xs: 8 }}
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  px={3}
                >
                  <Typography color="#EC6E00" fontWeight={700} fontSize={16}>
                    Total Cash Payable
                  </Typography>
                  <Typography fontWeight={600}>R{totalCash}</Typography>
                </Grid>
              )}
              {totalService !== 0 && (
                <Grid
                  size={{ xs: 8 }}
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  px={3}
                >
                  <Typography fontWeight={700} fontSize={16} color="#EC6E00">
                    Total Service Payable
                  </Typography>
                  <Typography fontWeight={600}>R{totalService}</Typography>
                </Grid>
              )}

              <Grid size={{ xs: 6 }}></Grid>
            </Grid>
          </Box>
          <Grid container spacing={5}>
            {exGratiaFile?.map((doc: any, index: number) => (
              <Grid size={{ xs: 12, md: 4 }}>
                <Box>
                  <Typography
                    fontWeight={600}
                    textAlign="left"
                    fontSize={18}
                    mb={1}
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ex-Gratia Approve
                  </Typography>
                  <Box
                    key={index}
                    sx={{
                      background: "#F5EEE9",
                      color: "#FF0000",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexGrow={1}
                    >
                      <PictureAsPdfIcon />
                      <Box>
                        <Typography fontSize={14}>{doc.file_name}</Typography>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                        ></Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={() => handlePreview(doc)} size="small">
                      <VisibilityIcon sx={{ color: "#EC6E00" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}
            {cashReciept?.map((doc: any, index: number) => (
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <Box>
                  <Typography
                    fontWeight={600}
                    textAlign="left"
                    fontSize={18}
                    mb={1}
                  >
                    Cash Reciept
                  </Typography>
                  <Box
                    key={index}
                    sx={{
                      background: "#F5EEE9",
                      color: "#FF0000",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexGrow={1}
                    >
                      <PictureAsPdfIcon />
                      <Box>
                        <Typography fontSize={14}>{doc.file_name}</Typography>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                        ></Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={() => handlePreview(doc)} size="small">
                      <VisibilityIcon sx={{ color: "#EC6E00" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}

            {serviceReciept?.map((doc: any, index: number) => (
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <Box>
                  <Typography
                    fontWeight={600}
                    textAlign="left"
                    fontSize={18}
                    mb={1}
                  >
                    Service Reciept
                  </Typography>
                  <Box
                    key={index}
                    sx={{
                      background: "#F5EEE9",
                      color: "#FF0000",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexGrow={1}
                    >
                      <PictureAsPdfIcon />
                      <Box>
                        <Typography fontSize={14}>{doc.file_name}</Typography>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                        ></Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={() => handlePreview(doc)} size="small">
                      <VisibilityIcon sx={{ color: "#EC6E00" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}

            {repudiationFile?.map((doc: any, index: number) => (
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <Box>
                  <Typography
                    fontWeight={600}
                    textAlign="left"
                    fontSize={18}
                    mb={1}
                  >
                    Repudiation Letter
                  </Typography>
                  <Box
                    key={index}
                    sx={{
                      background: "#F5EEE9",
                      color: "#FF0000",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexGrow={1}
                    >
                      <PictureAsPdfIcon />
                      <Box>
                        <Typography fontSize={14}>{doc.file_name}</Typography>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                        ></Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={() => handlePreview(doc)} size="small">
                      <VisibilityIcon sx={{ color: "#EC6E00" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {claimStatus === "DECLINED" && statusReason && (
            <Box
              sx={{
                backgroundColor: "#F5EEE9",
                borderRadius: 2,
                p: 2,
                my: 3,
                border: "1px solid #E0E0E0",
                textAlign: "left",
              }}
            >
              <Typography fontWeight={600} fontSize={16} color="error.main">
                Status : Declined
              </Typography>

              <Typography mt={1} fontSize={14}>
                <strong>Reason:</strong> {statusReason}
              </Typography>
            </Box>
          )}

          {comments?.length > 0 && (
            <Grid size={12} mt={4}>
              {comments.map((comment: any, index: number) => (
                <Accordion
                  key={index}
                  sx={{
                    backgroundColor: "#F7F7F7",
                    boxShadow: "none",
                    borderRadius: "6px",
                    mb: 2,
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600} fontSize={14}>
                        {comment.commentedByName}{" "}
                        <Typography
                          component="span"
                          fontWeight={600}
                          color="#EC6E00"
                        >
                          - {comment.role_at_time_of_comment?.toUpperCase()}
                        </Typography>
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {new Date(comment.created_on).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                          },
                        )}{" "}
                        | {formatDate(comment.created_on)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      fontSize={15}
                      color="#333"
                      lineHeight={1.6}
                      textAlign="left"
                    >
                      {comment.content}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          )}
          {isAssigned && claimStatus !== "PAID" && (
            <Grid size={{ md: 12 }}>
              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={18}
                sx={{ display: "block", textAlign: "left" }}
              >
                Comment
              </Typography>
              <TextField
                multiline
                rows={4}
                variant="outlined"
                fullWidth
                disabled={isEditable}
                value={claimBenefitComment}
                onChange={onClaimBenefitCommentChange}
              />
            </Grid>
          )}

          {/* ---------------- Override Benefit Schedule Dialog (refined) ---------------- */}
          <Dialog
            open={overrideOpen}
            onClose={closeOverrideDialog}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                width: { xs: "calc(100% - 24px)", sm: 760 },
                maxWidth: 760,
                maxHeight: "92vh",
                borderRadius: "18px",
                boxShadow: "0 24px 64px rgba(20, 20, 20, 0.22)",
                overflow: "hidden",
              },
            }}
            BackdropProps={{
              sx: {
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(20, 20, 20, 0.45)",
              },
            }}
          >
            {/* Header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "9px",
                      bgcolor: ifasColors.accentSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <EditNoteIcon sx={{ fontSize: 18, color: ifasColors.accent }} />
                  </Box>
                  <Box textAlign="left">
                      <Typography fontWeight={700} fontSize={18} color={ifasColors.textPrimary}>
                      Override benefit schedule
                    </Typography>
                    <Typography fontSize={12.5} color={ifasColors.textMuted} mt="2px">
                      {iceClaimNumber || claimId}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={closeOverrideDialog}
                  size="small"
                  disabled={overrideSaving}
                  aria-label="Close"
                >
                  <CloseIcon sx={{ fontSize: 18, color: ifasColors.textMuted }} />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ borderColor: ifasColors.divider }} />

            {/* Audit strip */}
            <Box
              sx={{
                px: 3,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: ifasColors.bgSubtle,
              }}
            >
              <ShieldOutlinedIcon sx={{ fontSize: 14, color: ifasColors.textMuted }} />
              <Typography fontSize={12} color={ifasColors.textMuted}>
                 Submission and approval decision are recorded in the audit trail
              </Typography>
            </Box>

            <Divider sx={{ borderColor: ifasColors.divider }} />

            {/* Body */}
            <DialogContent
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2.5,
                bgcolor: ifasColors.pageBg,
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D8D5CE",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#C2BEB5",
                },
                scrollbarWidth: "thin",
                scrollbarColor: "#D8D5CE transparent",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: `1px solid ${ifasColors.border}`,
                  borderRadius: "14px",
                  p: { xs: 2, sm: 2.75 },
                }}
              >
              {/* Correction */}
              <SectionHeader icon={<TuneIcon fontSize="inherit" />} title="Correction" />
              <Grid container spacing={1.25} mb={3.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                    Action
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={overrideAction}
                    disabled={isInceptionCorrection}
                    onChange={(e) => {
                      setOverrideAction(e.target.value);
                      setSelectedBenefitId("");
                      setCorrectedCash("");
                      setCorrectedService("");
                      setCorrectedInceptionDate("");
                      inceptionRequestRef.current += 1;
                      setInceptionContext(null);
                      setInceptionContextError("");
                      setNewBenefitName("");
                    }}
                  >
                    <MenuItem value="AMEND_AMOUNT">
                      {isInceptionCorrection ? "Correct inception date" : "Amend amount / payout type"}
                    </MenuItem>
                    <MenuItem value="ADD_BENEFIT">Add missing benefit</MenuItem>
                    <MenuItem value="REMOVE_BENEFIT">Remove benefit</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                    Reason code
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={reasonCode}
                    onChange={(e) => {
                      const nextReason = e.target.value;
                      setReasonCode(nextReason);
                      const isDateReason = nextReason === "RC_01" || nextReason === "RC_02";
                      if (isDateReason) {
                        setOverrideAction("AMEND_AMOUNT");
                        if (selectedBenefitId) loadInceptionContext(selectedBenefitId);
                      } else {
                        inceptionRequestRef.current += 1;
                        setCorrectedInceptionDate("");
                        setInceptionContext(null);
                        setInceptionContextError("");
                        setInceptionContextLoading(false);
                      }
                    }}
                  >
                    <MenuItem value="RC_01">RC-01 Inception date reset</MenuItem>
                    <MenuItem value="RC_02">RC-02 Inception date error</MenuItem>
                    <MenuItem value="RC_03">RC-03 Phantom benefit</MenuItem>
                    <MenuItem value="RC_04">RC-04 Duplicate benefit</MenuItem>
                    <MenuItem value="RC_05">RC-05 Incorrect product</MenuItem>
                    <MenuItem value="RC_06">RC-06 Missing benefit</MenuItem>
                    <MenuItem value="RC_07">RC-07 Incorrect cover amount</MenuItem>
                    <MenuItem value="RC_08">RC-08 Other</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {/* Benefit line / new benefit */}
              <SectionHeader
                icon={<ChecklistIcon fontSize="inherit" />}
                title={overrideAction === "ADD_BENEFIT" ? "New benefit" : "Benefit line"}
              />
              <Box mb={3.5}>
                {overrideAction === "ADD_BENEFIT" ? (
                  <Grid container spacing={1.25}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                        Type
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={newBenefitType}
                        onChange={(e) => setNewBenefitType(e.target.value)}
                      >
                        <MenuItem value="Product">Product</MenuItem>
                        <MenuItem value="Supplementary">Supplementary</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                        Benefit name
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Inkomo top-up"
                        value={newBenefitName}
                        onChange={(e) => setNewBenefitName(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={selectedBenefitId}
                      onChange={(e) => {
                        setSelectedBenefitId(e.target.value);
                        const benefit = claimBenefitInfo?.find(
                          (item: any) => getBenefitRowId(item) === e.target.value,
                        );
                        setCorrectedCash(benefit?.cash || "");
                        setCorrectedService(benefit?.service || "");
                        if (isInceptionCorrection) {
                          loadInceptionContext(e.target.value);
                        } else {
                          setCorrectedInceptionDate("");
                          setInceptionContext(null);
                          setInceptionContextError("");
                        }
                      }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value: any) => {
                          if (!value) {
                            return (
                              <Typography fontSize={14} color={ifasColors.textMuted}>
                                Select the benefit this correction applies to
                              </Typography>
                            );
                          }
                          const item = claimBenefitInfo?.find(
                            (b: any) => getBenefitRowId(b) === value,
                          );
                          return item?.product_name || item?.supplementary_name || value;
                        },
                      }}
                    >
                      {!selectableOverrideBenefits.length && (
                        <MenuItem value="" disabled>
                          {overrideAction === "REMOVE_BENEFIT"
                            ? "No removable supplementary benefits found"
                            : "No benefits found on this claim"}
                        </MenuItem>
                      )}
                      {selectableOverrideBenefits.map((item: any) => {
                        const id = getBenefitRowId(item);
                        return (
                          <MenuItem value={id} key={id}>
                            {item.product_name || item.supplementary_name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </>
                )}
              </Box>

              {isInceptionCorrection && overrideAction === "AMEND_AMOUNT" && (
                <Box mb={3.5}>
                  <SectionHeader
                    icon={<ShieldOutlinedIcon fontSize="inherit" />}
                    title="Inception date correction"
                    hint="eligibility recalculates as you edit"
                  />
                  {inceptionContextLoading && (
                    <Box sx={{ p: 1.5, borderRadius: "9px", bgcolor: ifasColors.neutralSoft }}>
                      <Typography fontSize={13} color={ifasColors.textSecondary}>
                        Loading latest policy inception date from Easipol...
                      </Typography>
                    </Box>
                  )}
                  {!!inceptionContextError && !inceptionContextLoading && (
                    <Box sx={{ p: 1.5, borderRadius: "9px", bgcolor: ifasColors.dangerSoft, border: `1px solid ${ifasColors.dangerBorder}` }}>
                      <Typography fontSize={13} color={ifasColors.danger} fontWeight={700}>
                        {inceptionContextError}
                      </Typography>
                      <Typography fontSize={11.5} color={ifasColors.textSecondary} mt={0.5}>
                        This override cannot be submitted until the latest Easipol data is available.
                      </Typography>
                    </Box>
                  )}
                  {inceptionContext && !inceptionContextLoading && (
                    <>
                      <Box
                        sx={{
                          border: `1px solid ${ifasColors.border}`,
                          borderRadius: "12px",
                          overflow: "hidden",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            bgcolor: ifasColors.successSoft,
                            borderBottom: `1px solid ${ifasColors.successBorder}`,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 17, color: ifasColors.success }} />
                            <Typography fontSize={12.5} fontWeight={700} color={ifasColors.success}>
                              Latest policy data loaded from Easipol
                            </Typography>
                          </Box>
                          <Typography fontSize={11.5} color={ifasColors.textSecondary}>
                            Policy {inceptionContext.policy_number || "-"}
                          </Typography>
                        </Box>

                        <Grid container spacing={1.5} sx={{ p: 2 }} alignItems="stretch">
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box
                              sx={{
                                height: "100%",
                                p: 1.5,
                                borderRadius: "10px",
                                bgcolor: ifasColors.neutralSoft,
                                border: `1px solid ${ifasColors.neutralBorder}`,
                                textAlign: "left",
                              }}
                            >
                              <Typography fontSize={10.5} fontWeight={700} color={ifasColors.textMuted} letterSpacing={0.5}>
                                LATEST EASIPOL DATE
                              </Typography>
                              <Typography fontSize={18} fontWeight={700} color={ifasColors.textPrimary} mt={0.5}>
                                {inceptionContext.easipol_inception_date || "-"}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.textSecondary} mt={0.35}>
                                Read-only source value
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box
                              sx={{
                                height: "100%",
                                p: 1.5,
                                borderRadius: "10px",
                                bgcolor: ifasColors.accentSoft,
                                border: `1px solid ${ifasColors.accentBorder}`,
                                textAlign: "left",
                              }}
                            >
                              <Typography fontSize={10.5} fontWeight={700} color={ifasColors.accent} letterSpacing={0.5} mb={0.65}>
                                EFFECTIVE DATE USED FOR CALCULATION
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                value={correctedInceptionDate}
                                onChange={(e) => setCorrectedInceptionDate(e.target.value)}
                                inputProps={{ max: inceptionContext.date_of_death, "aria-label": "Effective inception date" }}
                                sx={{ bgcolor: "#fff", borderRadius: "8px" }}
                              />
                              <Typography fontSize={11.5} color={ifasColors.textSecondary} mt={0.6}>
                                Editable by Claims Manager
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box sx={{ px: 2, pb: 2 }}>
                          <Grid container spacing={1}>
                            {[
                              ["Date of death", inceptionContext.date_of_death || "-"],
                              ["Waiting period", `${inceptionContext.waiting_period_months} months`],
                              ["Completed", `${previewCompletedMonths ?? "-"} months`],
                            ].map(([label, value]) => (
                              <Grid size={{ xs: 12, sm: 4 }} key={label}>
                                <Box sx={{ p: 1.15, borderRadius: "9px", bgcolor: ifasColors.bgSubtle, border: `1px solid ${ifasColors.divider}`, textAlign: "left" }}>
                                  <Typography fontSize={10.5} color={ifasColors.textMuted}>{label}</Typography>
                                  <Typography fontSize={13.5} fontWeight={700} color={ifasColors.textPrimary} mt={0.2}>{value}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>

                          {previewUnderWaitingPeriod !== null && (
                            <Box
                              sx={{
                                mt: 1.25,
                                p: 1.4,
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: previewUnderWaitingPeriod ? ifasColors.dangerSoft : ifasColors.successSoft,
                                border: `1px solid ${previewUnderWaitingPeriod ? ifasColors.dangerBorder : ifasColors.successBorder}`,
                              }}
                            >
                              {previewUnderWaitingPeriod
                                ? <CancelOutlinedIcon sx={{ color: ifasColors.danger, fontSize: 21 }} />
                                : <CheckCircleOutlineIcon sx={{ color: ifasColors.success, fontSize: 21 }} />}
                              <Box textAlign="left">
                                <Typography
                                  fontSize={13.5}
                                  fontWeight={800}
                                  color={previewUnderWaitingPeriod ? ifasColors.danger : ifasColors.success}
                                >
                                  {previewUnderWaitingPeriod ? "Still under waiting period" : "Eligible after correction"}
                                </Typography>
                                <Typography fontSize={11.5} color={ifasColors.textSecondary}>
                                  Live preview only. The backend recalculates before saving the override.
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "9px", bgcolor: "#F7F7F5", display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        <Typography fontSize={12} color={ifasColors.textSecondary}>
                          Latest benefit calculation from Easipol policy data
                        </Typography>
                        <Typography fontSize={12} fontWeight={700} color={ifasColors.textPrimary}>
                          {recalculatedAmounts
                            ? `Cash R${recalculatedAmounts.cash} · Service R${recalculatedAmounts.service}`
                            : "Amount calculation unavailable"}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Corrected values */}
              {overrideAction !== "REMOVE_BENEFIT" && !isInceptionCorrection && (
                <>
                  <SectionHeader
                    icon={<AttachMoneyIcon fontSize="inherit" />}
                    title="Corrected values"
                    hint="blank keeps current value"
                  />
                  <Grid container spacing={1.25} mb={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                        Cash
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="0"
                        value={correctedCash}
                        onChange={(e) => setCorrectedCash(e.target.value)}
                        disabled={isInceptionCorrection || !allowsCashCorrection}
                        inputProps={{ min: 0, step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <Typography fontSize={14} color={ifasColors.textMuted} mr={0.5}>
                              R
                            </Typography>
                          ),
                        }}
                      />
                      {overrideAction !== "ADD_BENEFIT" && (
                        <Typography fontSize={11} color={ifasColors.textMuted} mt={0.5} textAlign="left">
                          {allowsCashCorrection
                            ? `Current: R${selectedBenefit?.cash || 0}`
                            : "Not available for this benefit"}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography fontSize={11} color={ifasColors.textMuted} mb={0.5} textAlign="left">
                        Service
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="0"
                        value={correctedService}
                        onChange={(e) => setCorrectedService(e.target.value)}
                        disabled={isInceptionCorrection || !allowsServiceCorrection}
                        inputProps={{ min: 0, step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <Typography fontSize={14} color={ifasColors.textMuted} mr={0.5}>
                              R
                            </Typography>
                          ),
                        }}
                      />
                      {overrideAction !== "ADD_BENEFIT" && (
                        <Typography fontSize={11} color={ifasColors.textMuted} mt={0.5} textAlign="left">
                          {allowsServiceCorrection
                            ? `Current: R${selectedBenefit?.service || 0}`
                            : "Not available for this benefit"}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Justification */}
              <SectionHeader icon={<ChatBubbleOutlineIcon fontSize="inherit" />} title="Justification" />
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Explain why this correction is needed, referencing the evidence attached"
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                sx={{ mb: 1.5 }}
              />

              {evidenceFile ? (
                <Box
                  sx={{
                    height: 42,
                    px: 1.5,
                    borderRadius: "9px",
                    border: `0.5px solid ${ifasColors.successBorder}`,
                    bgcolor: ifasColors.successSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: ifasColors.success }} />
                    <Typography
                      fontSize={13}
                      color={ifasColors.success}
                      noWrap
                      title={evidenceFile.name}
                    >
                      {evidenceFile.name}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setEvidenceFile(null)} aria-label="Remove file">
                    <CloseIcon sx={{ fontSize: 15, color: ifasColors.success }} />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  fullWidth
                  sx={{
                    height: 42,
                    justifyContent: "flex-start",
                    textTransform: "none",
                    borderRadius: "9px",
                    border: `0.5px dashed ${ifasColors.border}`,
                    color: ifasColors.textSecondary,
                    fontSize: 13,
                    px: 1.5,
                    "&:hover": { borderColor: ifasColors.accent, bgcolor: ifasColors.accentSoft },
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, mr: 1 }} />
                  Attach evidence
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
                  />
                </Button>
              )}
              </Box>
            </DialogContent>

            <Divider sx={{ borderColor: ifasColors.divider }} />

            {/* Footer */}
            <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
              <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                {isOverrideReadyToSubmit ? (
                  <>
                    <CheckCircleOutlineIcon sx={{ fontSize: 15, color: ifasColors.success }} />
                    <Typography fontSize={12.5} color={ifasColors.textSecondary}>
                      Ready to submit
                    </Typography>
                  </>
                ) : (
                  <Typography
                    fontSize={12}
                    color={ifasColors.textMuted}
                    textAlign="left"
                    title={`Missing: ${missingFields.join(", ")}`}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 220,
                    }}
                  >
                    {missingFields.length} field{missingFields.length > 1 ? "s" : ""} remaining
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1.25} flexShrink={0}>
                <Button
                  variant="outlined"
                  onClick={closeOverrideDialog}
                  disabled={overrideSaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 2.5,
                    py: 1,
                    bgcolor: "#fff",
                    color: ifasColors.textSecondary,
                    borderColor: ifasColors.border,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#F5F4F1",
                      borderColor: ifasColors.border,
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#fff",
                      color: ifasColors.textMuted,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitOverride}
                  disabled={overrideSaving || !isOverrideReadyToSubmit}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 2.75,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {overrideSaving ? "Saving..." : "Submit override"}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          <Dialog
            open={overrideHistoryOpen}
            onClose={() => setOverrideHistoryOpen(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: "14px" } }}
          >
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "9px",
                      bgcolor: ifasColors.accentSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ChecklistIcon sx={{ fontSize: 18, color: ifasColors.accent }} />
                  </Box>
                  <Typography fontWeight={500} fontSize={16} color={ifasColors.textPrimary}>
                    Override history
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOverrideHistoryOpen(false)} aria-label="Close">
                  <CloseIcon sx={{ fontSize: 18, color: ifasColors.textMuted }} />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ borderColor: ifasColors.divider }} />
            <DialogContent
              sx={{
                backgroundColor: ifasColors.pageBg,
                px: 3,
                py: 2.5,
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#D8D5CE", borderRadius: "10px" },
                "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#C2BEB5" },
                scrollbarWidth: "thin",
                scrollbarColor: "#D8D5CE transparent",
              }}
            >
              {overrideHistoryLoading ? (
                <Typography color={ifasColors.textSecondary}>Loading history...</Typography>
              ) : overrideHistory.length === 0 ? (
                <Typography color={ifasColors.textSecondary}>
                  No benefit schedule overrides have been recorded for this claim.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {overrideHistory.map((entry: any) => {
                    const status = entry.status;
                    const meta = overrideStatusMeta[status] || {
                      color: ifasColors.neutral,
                      soft: ifasColors.neutralSoft,
                      border: ifasColors.neutralBorder,
                      icon: <ShieldOutlinedIcon sx={{ fontSize: 16 }} />,
                    };
                    return (
                      <Box
                        key={entry.override_id}
                        sx={{
                          p: 1.75,
                          borderRadius: "12px",
                          backgroundColor: "#FFFFFF",
                          border: `1px solid ${ifasColors.border}`,
                          borderLeft: `4px solid ${meta.color}`,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          spacing={1}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              icon={meta.icon}
                              label={formatOverrideStatus(status)}
                              sx={{
                                backgroundColor: meta.soft,
                                color: meta.color,
                                fontWeight: 800,
                                border: `1px solid ${meta.border}`,
                                "& .MuiChip-icon": { color: meta.color },
                              }}
                            />
                            <Chip
                              size="small"
                              label={entry.risk_tier || "-"}
                              sx={{ backgroundColor: ifasColors.pageBg, fontWeight: 700, border: `1px solid ${ifasColors.border}` }}
                            />
                            <Chip
                              size="small"
                              label={entry.reason_code || "-"}
                              sx={{ backgroundColor: ifasColors.pageBg, fontWeight: 700, border: `1px solid ${ifasColors.border}` }}
                            />
                          </Stack>
                          <Typography variant="caption" color={ifasColors.textMuted}>
                            {formatOverrideDate(
                              entry.reviewed_at || entry.approved_at || entry.created_on,
                            )}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="body2"
                          color={ifasColors.textPrimary}
                          sx={{ mt: 1, fontWeight: 600 }}
                        >
                          {entry.items?.[0]?.action
                            ? actionLabels[entry.items[0].action] || entry.items[0].action
                            : "Benefit schedule correction"}
                        </Typography>
                        <Typography variant="body2" color={ifasColors.textSecondary} sx={{ mt: 0.4 }}>
                          {entry.motivation || "-"}
                        </Typography>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          sx={{ mt: 1.25 }}
                        >
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              label={`Cash: R${entry.corrected_cash_total ?? 0}`}
                              sx={{ backgroundColor: ifasColors.accentSoft, fontWeight: 700 }}
                            />
                            <Chip
                              size="small"
                              label={`Service: R${entry.corrected_service_total ?? 0}`}
                              sx={{ backgroundColor: ifasColors.accentSoft, fontWeight: 700 }}
                            />
                            {entry.review_note && (
                              <Chip
                                size="small"
                                label={`Review: ${entry.review_note}`}
                                sx={{ backgroundColor: ifasColors.pageBg, maxWidth: 260, border: `1px solid ${ifasColors.border}` }}
                              />
                            )}
                          </Stack>

                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography
                              variant="caption"
                              color={ifasColors.textMuted}
                              sx={{ maxWidth: 190 }}
                              noWrap
                              title={entry.evidence_file_name || ""}
                            >
                              {entry.evidence_file_name || "No evidence"}
                            </Typography>
                            {entry.evidence_document_id && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => previewOverrideEvidence(entry)}
                                  title="View evidence"
                                >
                                  <VisibilityIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => downloadOverrideEvidence(entry)}
                                  title="Download evidence"
                                >
                                  <PictureAsPdfIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </DialogContent>
            <Divider sx={{ borderColor: ifasColors.divider }} />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setOverrideHistoryOpen(false)}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  color: ifasColors.textSecondary,
                  border: `1px solid ${ifasColors.border}`,
                  px: 2.5,
                  py: 1,
                  "&:hover": { bgcolor: "#F5F4F1" },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openPreview}
            onClose={handleClosePreview}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Document Preview</DialogTitle>
            <DialogContent dividers>
              {selectedDoc ? (
                <object
                  data={selectedDoc.file_url}
                  type="application/pdf"
                  width="100%"
                  height="600px"
                >
                  <p>
                    PDF preview is not supported in this browser.{" "}
                    <a
                      href={selectedDoc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download PDF
                    </a>
                  </p>
                </object>
              ) : (
                <Typography>Loading...</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => handleDownload(selectedDoc)}
                color="primary"
                variant="outlined"
              >
                Download
              </Button>
              <Button
                onClick={handleClosePreview}
                color="secondary"
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}
